/**
 * Doing one thing to several files.
 *
 * There is no batch endpoint and this deliberately does not ask for one. Each
 * file goes through the same per-file route a single action uses, which means
 * the server re-establishes ownership for every id rather than trusting a list
 * a client sent - and it means a bulk action can refuse one file and perform
 * the rest, which is exactly what happens when a reconstruction is holding on
 * to one of them.
 *
 * What that costs is that "it worked" is no longer a single answer. So this
 * returns one outcome per file and the screen reports the partial result
 * honestly, rather than showing a success message over two silent failures.
 */

import { ApiError } from "@/api/errors";

/** How many files are acted on at once. Bounded for the same reason reads are. */
export const BULK_CONCURRENCY = 4;

export type BulkOutcome =
  | { id: string; ok: true }
  | { id: string; ok: false; error: ApiError };

export type BulkFailure = Extract<BulkOutcome, { ok: false }>;

export interface BulkResult {
  outcomes: BulkOutcome[];
  succeeded: string[];
  failed: BulkFailure[];
  /** True when the caller stopped it; the remaining files were not touched. */
  stopped: boolean;
}

export async function runBulk(
  ids: readonly string[],
  action: (id: string) => Promise<unknown>,
  options: { signal?: AbortSignal; onProgress?: (done: number) => void } = {},
): Promise<BulkResult> {
  const outcomes: BulkOutcome[] = [];
  let done = 0;
  let stopped = false;

  for (let offset = 0; offset < ids.length; offset += BULK_CONCURRENCY) {
    if (options.signal?.aborted) {
      stopped = true;
      break;
    }
    const batch = ids.slice(offset, offset + BULK_CONCURRENCY);
    const results = await Promise.all(
      batch.map(async (id): Promise<BulkOutcome> => {
        try {
          await action(id);
          return { id, ok: true };
        } catch (caught) {
          return {
            id,
            ok: false,
            error:
              caught instanceof ApiError
                ? caught
                : new ApiError({ kind: "unknown", url: "", cause: caught }),
          };
        }
      }),
    );
    outcomes.push(...results);
    done += results.length;
    options.onProgress?.(done);
  }

  return {
    outcomes,
    succeeded: outcomes.filter((outcome) => outcome.ok).map((outcome) => outcome.id),
    failed: outcomes.filter((outcome): outcome is BulkFailure => !outcome.ok),
    stopped,
  };
}

/**
 * Why one file could not be deleted, in the customer's words.
 *
 * `media_in_reconstruction` is the refusal that matters and the one a bulk
 * action makes visible: a finished result's input list names that file, and
 * removing it would leave the result describing frames nobody can look at.
 */
export function deleteFailureText(error: ApiError): string {
  if (error.code === "media_in_reconstruction") {
    return (
      "Bu dosya bir 3B rekonstrüksiyonun girdi listesinde. Silinirse tamamlanmış " +
      "bir sonuç, artık var olmayan karelere dayanır hâle gelirdi."
    );
  }
  switch (error.kind) {
    case "not_found":
      return "Bu dosya zaten yok.";
    case "network":
      return "Sunucuya ulaşılamadı. Dosya silinmedi.";
    case "timeout":
      return "Sunucu zamanında yanıt vermedi. Dosya silinmemiş olabilir.";
    default:
      return "Dosya silinemedi.";
  }
}

/** Why one file's capture angle could not be set. */
export function assignFailureText(error: ApiError): string {
  switch (error.kind) {
    case "not_found":
      return "Bu dosya artık yok.";
    case "validation":
      return "Bu açı bu proje türü için geçerli değil.";
    case "network":
      return "Sunucuya ulaşılamadı. Açı işaretlenmedi.";
    case "timeout":
      return "Sunucu zamanında yanıt vermedi. Açı işaretlenmemiş olabilir.";
    default:
      return "Açı işaretlenemedi.";
  }
}

/**
 * Why one file could not be enhanced.
 *
 * `not_found` is the one that carries the delete-safety rule. A photograph
 * removed while a batch was running - or a whole project deleted - answers 404
 * on its own request, which lands here as a plain sentence instead of a
 * derivative appearing under a file that no longer exists. Nothing has to be
 * cancelled for that to hold, because there is no queued work to cancel.
 */
export function enhanceFailureText(error: ApiError): string {
  if (error.status === 409) {
    return "Bu dosyada ölçülmüş ve güvenle düzeltilebilen bir sorun yok.";
  }
  if (error.status === 422) {
    return "Bu dosya güvenle iyileştirilemiyor.";
  }
  switch (error.kind) {
    case "not_found":
      return "Bu dosya artık yok.";
    case "network":
      return "Sunucuya ulaşılamadı. Dosya değişmedi.";
    case "timeout":
      return "Sunucu zamanında yanıt vermedi. İyileştirme oluşmamış olabilir.";
    default:
      return "İyileştirme oluşturulamadı; orijinal dosya değişmedi.";
  }
}

/**
 * The distinct reasons behind a set of failures, each with how many files hit
 * it.
 *
 * Grouped rather than listed per file: twenty rows all saying the same
 * sentence is a wall, and the reader's next action is the same for all twenty.
 */
export function groupFailures(
  failed: readonly BulkFailure[],
  describe: (error: ApiError) => string,
): { reason: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const outcome of failed) {
    const reason = describe(outcome.error);
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
}
